import { useState } from 'react'
import { ApiError, api, type KnownWorkerKind, type Worker, type WorkerKind } from '../api'
import { interval, isDue, since, until } from '../format'
import { haptic } from '../telegram'

const NAMES: Record<KnownWorkerKind, { title: string; hint: string }> = {
  Works: { title: 'Работы', hint: 'Забирает готовые и ставит новые' },
  Dice: { title: 'Кости', hint: 'Играет партии, пока хватает денег' },
  Businesses: { title: 'Бизнесы', hint: 'Собирает доход и улучшает' },
  Combat: { title: 'Бои', hint: 'Тратит энергию на подходящих соперников' },
  Bosses: { title: 'Боссы', hint: 'Бьёт боссов, пока есть попытки' },
}

/**
 * A kind this bundle was not built with still gets a row: the server runs it, so the user must be
 * able to see and stop it. The bare name is the best label available until the panel is updated.
 */
function describe(kind: WorkerKind): { title: string; hint: string } {
  return (NAMES as Record<string, { title: string; hint: string } | undefined>)[kind] ?? {
    title: kind,
    hint: 'Новый воркер — обновите панель, чтобы увидеть описание',
  }
}

const INTERVALS = [15, 30, 60, 120, 240, 480]

export function WorkersList({
  workers,
  licenseActive,
  accountEnabled,
  onChanged,
}: {
  workers: Worker[]
  licenseActive: boolean
  /** False when the admin paused the whole account; the rows keep their flags but nothing runs. */
  accountEnabled: boolean
  /** Reloads the list from the server; resolves once the fresh state is in place. */
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState<WorkerKind | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Not an error: the request went through, but the fresh state deserves a word (see the chip).
  const [notice, setNotice] = useState<string | null>(null)

  async function run(kind: WorkerKind, call: () => Promise<unknown>) {
    setBusy(kind)
    setError(null)
    setNotice(null)
    try {
      await call()
      haptic('tap')
      // Stay busy until the reload lands: between the PUT and the fresh list the row still shows the
      // old state, and a click in that window would act on a row the server has already changed.
      await onChanged()
    } catch (e) {
      haptic('error')
      if (e instanceof ApiError && e.isPaymentRequired) {
        setError('Подписка неактивна — включить воркер нельзя. Выключать можно всегда.')
      } else if (e instanceof ApiError && e.isUnavailable) {
        setError('Сервер запущен без базы данных: настройки воркеров недоступны.')
      } else if (e instanceof ApiError && e.isServerRestarting) {
        setError('Сервер перезапускается, попробуйте через минуту.')
      } else if (e instanceof ApiError && e.isOffline) {
        setError('Нет связи с сервером — настройка не сохранена. Попробуйте ещё раз.')
      } else if (e instanceof ApiError && e.code === 'account_not_connected') {
        setError('Сначала подключите аккаунт (выше) — иначе воркеру нечем входить в игру.')
      } else {
        setError(e instanceof Error ? e.message : 'Не удалось сохранить')
      }
    } finally {
      setBusy(null)
    }
  }

  return (
    <section>
      <h2>Воркеры</h2>

      {!accountEnabled && (
        <p className="banner bad">
          Аккаунт приостановлен администратором — воркеры не запускаются. Выключить их можно.
        </p>
      )}
      {accountEnabled && !licenseActive && (
        <p className="banner">
          Подписка неактивна: включить воркеры и менять интервал нельзя, выключить — можно.
        </p>
      )}
      {error && <p className="banner bad">{error}</p>}
      {notice && <p className="banner">{notice}</p>}

      {workers.map((worker) => {
        const meta = describe(worker.kind)
        const locked = busy === worker.kind
        // A worker on a paused account is configured but idle: no countdown, no interval to tune.
        const active = worker.enabled && accountEnabled
        const next = active ? until(worker.nextRunAtUtc, 'accusative') : null
        const dueNow = active && isDue(worker.nextRunAtUtc)
        const last = since(worker.lastRunAtUtc)

        return (
          <div className={`card worker ${accountEnabled ? '' : 'paused'}`} key={worker.kind}>
            <div className="row">
              <div className="grow">
                <div className="title">{meta.title}</div>
                <div className="muted">{meta.hint}</div>
              </div>

              <label className="switch">
                <input
                  type="checkbox"
                  checked={worker.enabled}
                  // Switching off is always possible: a lapsed user, or one the admin paused, must
                  // never be locked out of stopping their own farming.
                  // Switching on is not offered without a licence (the server refuses it) nor on a
                  // paused account, where the flag would change nothing the user can see.
                  disabled={locked || (!worker.enabled && (!accountEnabled || !licenseActive))}
                  onChange={(e) => run(worker.kind, () => api.setWorkerEnabled(worker.kind, e.target.checked))}
                />
                <span className="slider" />
              </label>
            </div>

            {active && (
              <>
                {/* An interval change is refused without a licence, so the chips are not offered
                    at all rather than answering every tap with a message about enabling. */}
                {licenseActive && (
                  <div className="chips">
                    {INTERVALS.map((minutes) => (
                      <button
                        key={minutes}
                        className={`chip ${worker.intervalMinutes === minutes ? 'on' : ''}`}
                        disabled={locked}
                        // Only the interval travels: the server keeps the enabled flag as it is, so a
                        // row rendered from stale state cannot re-enable what was switched off
                        // elsewhere in the meantime.
                        onClick={() =>
                          run(worker.kind, async () => {
                            const saved = await api.setWorkerInterval(worker.kind, minutes)
                            // The server answers with the flag it kept. "Off" for a row the panel
                            // showed as on means the worker was switched off since the last
                            // refresh (a disconnect, the admin — or a server that predates
                            // interval-only bodies and reads the missing flag as false). The reload will flip the
                            // switch; without a word the user would take that for a glitch.
                            if (saved?.enabled === false) {
                              setNotice('Интервал сохранён, но воркер сейчас выключен — включите его заново, если нужно.')
                            }
                          })
                        }
                      >
                        {interval(minutes)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="muted small">
                  {dueNow && <>Следующий запуск — сейчас. </>}
                  {next && <>Следующий запуск через {next}. </>}
                  {last && <>Последний — {last}.</>}
                </div>
              </>
            )}

            {worker.lastOutcome && <div className="muted small outcome">{worker.lastOutcome}</div>}

            {/* The scheduler does not disable a failing worker, it backs off: each failure doubles
                the wait up to a cap. Saying "приостановлен" would be a lie. */}
            {worker.consecutiveFailures > 0 && (
              <div className="small warn-text">
                Ошибок подряд: {worker.consecutiveFailures} — интервал временно увеличен
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
